import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { GlassesPackageDisplay } from '../GlassesPackageDisplay';
import { PricingOverview } from '../PricingOverview';
import { AddOnSelector } from '../AddOnSelector';
import { Sticker } from '../Sticker';
import { breakpoints, colors } from '../../constants';
import { IGlassesPackage, IFeature, IAddOn } from '../../types';

const animationVariants: Variants = {
    enter: (slideLeft: boolean) => ({
        x: slideLeft ? '-100%' : '100%',
        position: 'absolute',
    }),
    center: { x: 0, position: 'static' },
    exit: (slideLeft: boolean) => ({
        x: slideLeft ? '100%' : '-100%',
        position: 'absolute',
    }),
};

const PricingOverviewContainer = styled.div``;

interface IProps {
    className?: string;
    allFeatures: IFeature[];
    addOns: IAddOn[];
    selectedPackage: IGlassesPackage;
    selectedAddOns: IAddOn[];
    onAddOnClick(addOn: IAddOn): void;
}

const BaseGlassesPackageView = ({
    className,
    allFeatures,
    addOns,
    selectedPackage,
    selectedAddOns,
    onAddOnClick,
}: IProps) => {
    const { t } = useTranslation();
    const previousPackageRef = useRef(selectedPackage);

    const getSlideDirection = (): 'left' | 'right' => {
        if (
            selectedPackage.multifocal !== previousPackageRef.current.multifocal
        ) {
            return selectedPackage.multifocal ? 'right' : 'left';
        }

        return selectedPackage.order > previousPackageRef.current.order
            ? 'right'
            : 'left';
    };

    useEffect(() => {
        previousPackageRef.current = selectedPackage;
    }, [selectedPackage]);

    const slideLeft = getSlideDirection() === 'left';
    const showSticker =
        selectedPackage.topChoice ||
        (selectedPackage.name === 'Platinum' && !selectedPackage.multifocal);

    return (
        <div className={className}>
            <AnimatePresence custom={slideLeft}>
                <motion.div
                    animate="center"
                    custom={slideLeft}
                    exit="exit"
                    initial="enter"
                    key={selectedPackage.id}
                    transition={{ ease: 'easeInOut' }}
                    variants={animationVariants}
                >
                    <PricingOverviewContainer>
                        {showSticker && (
                            <Sticker topChoice={selectedPackage.topChoice}>
                                {selectedPackage.topChoice
                                    ? t('topChoice')
                                    : t('storeOnly')}
                            </Sticker>
                        )}
                        <PricingOverview
                            selectedAddOns={selectedAddOns}
                            selectedPackage={selectedPackage}
                        />
                        {!!addOns.length && (
                            <AddOnSelector
                                addOns={addOns}
                                selectedAddOns={selectedAddOns}
                                onAddOnClick={onAddOnClick}
                            />
                        )}
                    </PricingOverviewContainer>
                    <GlassesPackageDisplay
                        allFeatures={allFeatures}
                        selectedPackage={selectedPackage}
                    />
                </motion.div>
            </AnimatePresence>
        </div>
    );
};

export const GlassesPackageView = styled(BaseGlassesPackageView)`
    position: relative;
    overflow: hidden;

    ${PricingOverviewContainer} {
        position: relative;
        height: 100%;
        background-color: ${colors.white};
        padding-top: ${({ selectedPackage }) =>
            selectedPackage.topChoice && '15px'};
    }

    ${Sticker} {
        position: absolute;
        top: 0;
        right: 5%;
    }

    > div {
        width: 100%;
        padding: 20px;

        @media (${breakpoints.mediumMin}) {
            display: flex;
            min-height: auto;
            padding: 40px 80px;

            ${PricingOverviewContainer} {
                width: 55%;
                margin: 10px 40px 10px 10px;
                box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.1);
                padding-top: 0;
            }
        }
    }
`;
